/**
 * RAW Re-Develop Brackets — synthesise N pixel-aligned exposure brackets
 * from a single RAW file by rendering it at different EV stops.
 *
 * This is the **physically real** path to Arri-class dynamic range:
 * the linear sensor data in a 12-14 bit RAW already encodes ~12-14 stops.
 * The brackets are NOT synthesised — they are different developments of
 * the same scene-referred radiance the sensor captured. Mertens fusion
 * (or a tone-mapper) then collapses that range to a viewable image.
 *
 *   RAW (linear, 12-14 bit)
 *     ├─ develop @ -2 EV → bracket A (highlight-protect, shadows crushed)
 *     ├─ develop @  0 EV → bracket B (mid-tone correct)
 *     └─ develop @ +2 EV → bracket C (shadow-lift, highlights blown)
 *          └─ fuseBrackets(...) → 16-bit display-mapped HDR
 *
 * Status: Phase 2A skeleton — depends on libraw.js (Phase 1.5 / 3 in the
 *   roadmap). Public API kept stable so the merge gate can land before
 *   the decoder.
 */

/**
 * @typedef {object} RawDecoded
 *   Linear, scene-referred RGBA buffer straight from the RAW decoder.
 *   Pixel range is [0, 1] but conceptually unbounded — values above 1
 *   correspond to scene radiance brighter than the metered white point.
 * @prop {Float32Array} rgba   width*height*4, linear scene-referred
 * @prop {number} width
 * @prop {number} height
 * @prop {object} [meta]       camera-supplied metadata (white balance,
 *                             exposure, ISO, white level, black level)
 */

/**
 * Apply an EV (exposure value) offset to a linear RAW buffer. Pure scalar
 * multiply in linear-light space — no clipping. Caller decides whether
 * to clamp before display.
 *
 * @param {RawDecoded} raw
 * @param {number} ev          stops; +1 doubles brightness, -1 halves
 * @returns {RawDecoded}       new buffer (immutable input)
 */
export function applyEV(raw, ev) {
    const k = Math.pow(2, ev);
    const src = raw.rgba;
    const dst = new Float32Array(src.length);
    for (let i = 0; i < src.length; i += 4) {
        dst[i]     = src[i]     * k;
        dst[i + 1] = src[i + 1] * k;
        dst[i + 2] = src[i + 2] * k;
        dst[i + 3] = src[i + 3];           /* alpha untouched */
    }
    return { rgba: dst, width: raw.width, height: raw.height, meta: raw.meta };
}

/**
 * Standard 3-bracket synthesis from one RAW: -2 / 0 / +2 EV.
 * Override `evStops` to tune how aggressively the brackets spread.
 *
 * @param {RawDecoded} raw
 * @param {number[]} [evStops=[-2, 0, 2]]
 * @returns {RawDecoded[]}
 */
export function synthesizeBrackets(raw, evStops = [-2, 0, 2]) {
    if (!raw || !raw.rgba) throw new Error('synthesizeBrackets: invalid raw');
    return evStops.map((ev) => applyEV(raw, ev));
}

/**
 * Decode a RAW container (DNG / ARW / CR3 / NEF / …). Lazy-loads
 * libraw.js the first time it's called; the caller MUST surface a
 * progress UI for the ~4 MB WASM download.
 *
 * @param {ArrayBuffer} _bytes
 * @returns {Promise<RawDecoded>}
 */
export async function decodeRaw(_bytes) {
    /* Skeleton. Wiring will:
     *   1. Lazy import('../decoders/libraw.js') — vendored under MIT.
     *   2. libraw.open(bytes, { use_camera_wb: true, output_bps: 16 }).
     *   3. Return Float32 RGBA in scene-referred linear, plus EXIF meta.
     *
     * We do NOT premultiply by exposure or apply the camera tone curve —
     * the caller pipes through synthesizeBrackets + fuseBrackets next. */
    throw new Error('decodeRaw: libraw.js not yet vendored (Phase 2A / 1.5)');
}

/**
 * End-to-end: RAW bytes → fused HDR float buffer ready for tone-mapping.
 * Convenience wrapper for the editor's "HDR from RAW" entry point.
 *
 * @param {ArrayBuffer} bytes
 * @param {object} [opts]
 * @returns {Promise<{ rgba: Float32Array, width: number, height: number }>}
 */
export async function rawToHDR(bytes, opts = {}) {
    const raw = await decodeRaw(bytes);
    const brackets = synthesizeBrackets(raw, opts.evStops);
    /* Avoid a circular import at module load — late import keeps the
     * Mertens module independently testable. */
    const { fuseBrackets } = await import('./mertens.js');
    return fuseBrackets(brackets, opts.fuse);
}
